import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import { Link } from '../../../RouterCompatibility';
import classNames from 'classnames';
import OfferPagePromo from '../OfferPagePromo';
import CheckContent from '../../../CheckContent';
import OfferPageGoalsComponent from '../OfferPageGoals/OfferPageGoals.container';
import LinkConstructorComponent from '../../../constructors/Link/LinkConstructor.container';
import PublisherActions from '../../../../actions/PublisherActions';
import PromoCodeConstructorComponent from "../../../constructors/PromoCode/PromoCodeConstructor.container";
import PromoCodeListConstructorComponent from "../../../constructors/PromoCodeList/PromoCodeListConstructor.container";

const OfferPageContainer = ({ 
    params, 
    location, 
    offer, 
    isEmpty, 
    isPaused, 
    loading, 
    searchGoalEntries, 
    searchLinks, 
    saveLinkPresets, 
    savePromoCode, 
    validatePromoCode,
    editPromoCode,
    deletePromoCode, 
    t 
}) => {
    const [state, setState] = useState({
        offer: offer || {},
        isEmpty: isEmpty,
        isPaused: isPaused,
        loading: loading,
        constructorLinkIsVisible: false,
        constructorPromocodeIsVisible: false,
        constructorPromocodeListIsVisible: false,
        creativeType: '',
        creativeId: '',
        goalEntryId: '',
        type: 'link',
        codeParams: {},
        linkId: '',
        promocode: '',
        link: {},
        access: true,
        returnToPromoCodeList: false,
        promoCodeListContext: null
    });

    const searchParams = new URLSearchParams(location?.search || '');
    const currentTab = searchParams.get('tab') || 'goals';

    const getClassTabItem = useCallback((key) => {
        return classNames({
            'active': currentTab === key
        });
    }, [currentTab]);

    const getClassTabPane = useCallback((key) => {
        return classNames({
            'tab-pane': true,
            'active': currentTab === key
        });
    }, [currentTab]);

    useEffect(() => {
        PublisherActions.getOffers({ids: params.id});
        searchGoalEntries(params.id);
        searchLinks({
            offer_ids: params.id
        });
    }, [params.id, searchGoalEntries, searchLinks]);

    useEffect(() => {
        const defaultLink = offer?.default_link || {};
        
        setState(prevState => ({
            ...prevState,
            offer: offer || {},
            linkId: prevState.linkId || defaultLink.id,
            loading: loading,
            isEmpty: isEmpty,
            isPaused: isPaused
        }));
    }, [offer, loading, isEmpty, isPaused]);

    useEffect(() => {
        setState((prevState) => {
            if (!prevState.constructorLinkIsVisible && !prevState.constructorPromocodeIsVisible && !prevState.constructorPromocodeListIsVisible) {
                return prevState;
            }

            return {
                ...prevState,
                constructorLinkIsVisible: false,
                constructorPromocodeIsVisible: false,
                constructorPromocodeListIsVisible: false,
                returnToPromoCodeList: false
            };
        });
    }, [location?.pathname, location?.search]);

    const showConstructor = useCallback((params = {}) => {
        setState(prevState => ({
            ...prevState,
            creativeType: params.creativeType,
            creativeId: params.creativeId,
            constructorLinkIsVisible: true,
            goalEntryId: params.goalEntryId,
            type: params.type || 'link',
            codeParams: params.codeParams,
            linkId: params.linkId,
            access: !(params.access === false)
        }));
    }, []);

    const showConstructorPromoCode = useCallback((params = {}) => {
        const shouldReturnToList = !!params.promocode;
        setState(prevState => ({
            ...prevState,
            creativeType: params.creativeType,
            creativeId: params.creativeId,
            constructorPromocodeIsVisible: true,
            constructorPromocodeListIsVisible: false,
            goalEntryId: params.goalEntryId,
            type: params.type || 'promocode',
            codeParams: params.codeParams,
            linkId: params.linkId,
            promocode: params.promocode,
            access: !(params.access === false),
            returnToPromoCodeList: shouldReturnToList
        }));
    }, []);

    const showConstructorPromoCodeList = useCallback((params = {}) => {
        setState(prevState => {
            const context = {
                creativeType: params.creativeType,
                creativeId: params.creativeId,
                goalEntryId: params.goalEntryId,
                type: params.type || 'promocode',
                codeParams: params.codeParams,
                linkId: (params.link && params.link.id) || prevState.linkId,
                link: params.link,
                access: !(params.access === false)
            };
            return {
                ...prevState,
                ...context,
                constructorPromocodeListIsVisible: true,
                returnToPromoCodeList: false,
                promoCodeListContext: context
            };
        });
    }, []);

    const onClosePromocodeListConstructor = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            constructorPromocodeListIsVisible: false,
            returnToPromoCodeList: false
        }));
    }, []);

    const onCloseLinkConstructor = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            constructorLinkIsVisible: false
        }));
    }, []);

    const onClosePromocodeConstructor = useCallback(() => {
        setState(prevState => {
            if (!prevState.returnToPromoCodeList) {
                return {
                    ...prevState,
                    constructorPromocodeIsVisible: false
                };
            }

            const context = prevState.promoCodeListContext || {
                creativeType: prevState.creativeType,
                creativeId: prevState.creativeId,
                goalEntryId: prevState.goalEntryId,
                type: prevState.type,
                codeParams: prevState.codeParams,
                linkId: prevState.linkId,
                link: prevState.link,
                access: prevState.access
            };

            return {
                ...prevState,
                ...context,
                constructorPromocodeIsVisible: false,
                constructorPromocodeListIsVisible: true,
                returnToPromoCodeList: false,
                promoCodeListContext: context
            };
        });
    }, []);

    const title = state.offer.title;
    const staticNames = t?.offersPage?.offer || {};
    const pathname = location?.pathname || '';

    return (
        <CheckContent
            loading={state.loading}
            isEmpty={state.isEmpty}
        >
            <div className="ibox offer-item">
                <div className="ibox-content">
                    <ul className="nav nav-tabs" id="myTab">
                        <li className={getClassTabItem('goals')}>
                            <Link to={`${pathname}?tab=goals`}>{staticNames.goalsTitle}</Link>
                        </li>
                        <li className={getClassTabItem('promo')}>
                            <Link to={`${pathname}?tab=promo`}>{staticNames.promoTitle}</Link>
                        </li>
                    </ul>
                    <div className="tab-content">
                        <div className={getClassTabPane('goals')}>
                            <OfferPageGoalsComponent />
                        </div>
                        <div className={getClassTabPane('promo')}>
                            <OfferPagePromo
                                location={location}
                                showConstructor={showConstructor}
                                showConstructorPromoCode={showConstructorPromoCode}
                                showConstructorPromoCodeList={showConstructorPromoCodeList}
                                params={params}
                            />
                        </div>
                    </div>

                    {!state.loading && state.offer.title
                        ?  <LinkConstructorComponent
                            creativeType={state.creativeType}
                            creativeId={state.creativeId}
                            offer={state.offer}
                            goalEntryId={state.goalEntryId}
                            linkId={state.linkId}
                            onClose={onCloseLinkConstructor}
                            visible={state.constructorLinkIsVisible}
                            access={state.access}
                            type={state.type}
                            codeParams={state.codeParams}
                            onSave={saveLinkPresets}
                          />
                        : null
                    }
                    {!state.loading && state.offer.title
                        ?  <PromoCodeConstructorComponent
                            creativeType="promocode"
                            creativeId={state.creativeId}
                            offer={state.offer}
                            goalEntryId={state.goalEntryId}
                            linkId={state.linkId}
                            promocode={state.promocode}
                            onClose={onClosePromocodeConstructor}
                            visible={state.constructorPromocodeIsVisible}
                            access={state.access}
                            type={state.type}
                            codeParams={state.codeParams}
                            onSave={savePromoCode}
                            onValidate={validatePromoCode}
                            onEdit={editPromoCode}
                        />
                        : null
                    }
                    {!state.loading && state.offer.title
                        ?  <PromoCodeListConstructorComponent
                            creativeType="link"
                            creativeId={state.creativeId}
                            offer={state.offer}
                            goalEntryId={state.goalEntryId}
                            linkId={state.linkId}
                            link={state.link}
                            onClose={onClosePromocodeListConstructor}
                            visible={state.constructorPromocodeListIsVisible}
                            access={state.access}
                            type={state.type}
                            codeParams={state.codeParams}
                            onDelete={deletePromoCode}
                            onEdit={editPromoCode}
                            showConstructorPromoCode={showConstructorPromoCode}
                        />
                        : null
                    }
                </div>
            </div>
        </CheckContent>
    );
};

OfferPageContainer.propTypes = {
    params: PropTypes.object,
    location: PropTypes.object,
    offer: PropTypes.object,
    isEmpty: PropTypes.bool,
    isPaused: PropTypes.bool,
    loading: PropTypes.bool,
    searchGoalEntries: PropTypes.func,
    searchLinks: PropTypes.func,
    saveLinkPresets: PropTypes.func,
    savePromoCode: PropTypes.func,
    validatePromoCode: PropTypes.func,
    editPromoCode: PropTypes.func,
    deletePromoCode: PropTypes.func,
    t: PropTypes.object
};

OfferPageContainer.defaultProps = {
    offer: {},
    isEmpty: false,
    isPaused: false,
    loading: false,
    location: {},
    searchGoalEntries: () => {},
    searchLinks: () => {},
    saveLinkPresets: () => {},
    savePromoCode: () => {},
    validatePromoCode: () => {},
    editPromoCode: () => {},
    deletePromoCode: () => {}
};

export default withTranslation(OfferPageContainer);
